import type { MySubscriptionView } from "../api/myBillingService";
import { expiryNotice } from "./expiryNotice";

/**
 * 1005:套餐到期后,用户看到的是「未开通」而不是「已到期」(演示机实测:到期扫描把订阅转 expired、
 * 计费舱权益全关 → 首页内容生产整块消失、直链计费舱落「功能未开通,联系我们开通」)。
 *
 * 判「套餐已到期」的数据源,按可信度取第一个有的:
 *  1. 租户超管:GET /tenant/billing/me 的 subscription(status=expired / 到期时刻已过,同 {@link expiryNotice});
 *  2. /me/permissions 的 `subscriptionStatus` / `subscriptionEndAt`(1005 向 api 提的可选字段,全员可读);
 *  3. 兜底(非超管 + 老后端):内容生产几舱**全部**落在「拥有过、现在关着、没被暂停」里,且一个都没开着
 *     —— 这正是到期扫描的形状(单个增值舱到期不会让整组内容舱全关)。拿不到日期,只说「已到期」。
 */
export const CONTENT_PRODUCTION_MODULES = [
  "ai_poster",
  "ai_video",
  "ai_digital_human",
  "ai_explosive_parse",
  "ai_reporter",
] as const;

export interface SubscriptionLapse {
  /** 订阅已到期(或被停用):首页常驻提示条据此出现。 */
  expired: boolean;
  /** 停用而非到期(文案换「已暂停」)。 */
  suspended: boolean;
  /** YYYY-MM-DD;拿不到(非超管且 api 没给)= null。 */
  endedOn: string | null;
}

export function subscriptionLapse(input: {
  adminSubscription?: MySubscriptionView | null;
  subscriptionStatus?: string | null;
  subscriptionEndAt?: string | null;
  lapsedModules: readonly string[];
  openModules: readonly string[];
  now?: Date;
}): SubscriptionLapse {
  const sub = input.adminSubscription;
  if (sub) {
    const suspended = sub.status === "suspended";
    const expired = suspended || expiryNotice(sub, input.now ?? new Date())?.kind === "expired";
    return {
      expired,
      suspended,
      endedOn: expired && sub.effectiveTo ? sub.effectiveTo.slice(0, 10) : null,
    };
  }
  const st = input.subscriptionStatus?.toLowerCase();
  if (st) {
    const suspended = st === "suspended";
    const expired = st === "expired" || suspended;
    return {
      expired,
      suspended,
      endedOn: expired && input.subscriptionEndAt ? input.subscriptionEndAt.slice(0, 10) : null,
    };
  }
  const lapsedContent = CONTENT_PRODUCTION_MODULES.filter((m) => input.lapsedModules.includes(m));
  const openContent = CONTENT_PRODUCTION_MODULES.filter((m) => input.openModules.includes(m));
  return {
    expired: lapsedContent.length > 0 && openContent.length === 0,
    suspended: false,
    endedOn: null,
  };
}

/** 首页提示条 / 到期落地页共用的一句话(联系人按直营「客户经理」/ 渠道「服务商」两套口径)。 */
export function lapseNoticeText(lapse: SubscriptionLapse, partnerTenant: boolean): string {
  const who = partnerTenant ? "服务商" : "客户经理";
  const head = lapse.suspended
    ? "套餐已暂停使用"
    : lapse.endedOn
      ? `套餐已于 ${lapse.endedOn} 到期`
      : "套餐已到期";
  return `${head},内容生产等功能已暂停;历史作品仍可在「我的作品」查看和下载。续费请联系您的${who}。`;
}
